import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { productService } from '../services/firestore';
import { useCart } from '../context/CartContext';
import { Product } from '../types';
import './ProductDetailPage.css';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addItem } = useCart();
  const [product, setProduct] = useState<Product | null>(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);
  const [showNotification, setShowNotification] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      if (!id) return;
      
      try {
        const productData = await productService.getById(id);
        setProduct(productData);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [id]);

  const handleQuantityChange = (newQuantity: number) => {
    if (product && newQuantity >= 1 && newQuantity <= product.stock) {
      setQuantity(newQuantity);
    }
  };

  const handleAddToCart = async () => {
    if (!product) return;
    
    setAddingToCart(true);
    try {
      addItem(product, quantity);
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
    } catch (error) {
      console.error('Error adding to cart:', error);
    } finally {
      setAddingToCart(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(price);
  };

  const calculateTotal = () => {
    if (!product) return 0;
    return product.price * quantity;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading"></div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="container">
        <div className="product-not-found">
          <h2>Product Not Found</h2>
          <p>The product you're looking for doesn't exist or has been removed.</p>
          <Link to="/products" className="btn btn-primary">
            Back to Products
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="product-detail-page">
      <div className="container">
        <nav className="breadcrumb">
          <Link to="/">Home</Link>
          <span className="separator">/</span>
          <Link to="/products">Products</Link>
          <span className="separator">/</span>
          <span className="current">{product.name}</span>
        </nav>

        <div className="product-detail">
          <div className="product-gallery">
            <div className="main-image">
              <img 
                src={product.imageUrl || ''} 
                alt={product.name}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          </div>

          <div className="product-info">
            <h1 className="product-title">{product.name}</h1>
            
            <div className="product-price-section">
              <span className="product-price">{formatPrice(product.price)}</span>
              <span className="price-per-unit">per piece</span>
            </div>

            <div className="product-description">
              <h3>Description</h3>
              <p>{product.description}</p>
            </div>

            <div className="stock-info">
              {product.stock > 0 ? (
                <span className="in-stock">
                  {product.stock} pieces available
                </span>
              ) : (
                <span className="out-of-stock">Out of Stock</span>
              )}
            </div>

            {product.stock > 0 && (
              <div className="purchase-section">
                <div className="quantity-selector">
                  <label htmlFor="quantity">Quantity:</label>
                  <div className="quantity-controls">
                    <button 
                      className="quantity-btn"
                      onClick={() => handleQuantityChange(quantity - 1)}
                      disabled={quantity <= 1}
                    >
                      -
                    </button>
                    <input 
                      id="quantity"
                      type="number" 
                      value={quantity}
                      onChange={(e) => handleQuantityChange(parseInt(e.target.value) || 1)}
                      min="1"
                      max={product.stock}
                    />
                    <button 
                      className="quantity-btn"
                      onClick={() => handleQuantityChange(quantity + 1)}
                      disabled={quantity >= product.stock}
                    >
                      +
                    </button>
                  </div>
                </div>

                <div className="total-price">
                  <span>Total: </span>
                  <span className="total-amount">{formatPrice(calculateTotal())}</span>
                </div>

                <div className="action-buttons">
                  <button 
                    className="btn btn-primary btn-large"
                    onClick={handleAddToCart}
                    disabled={addingToCart || product.stock === 0}
                  >
                    {addingToCart ? (
                      <>
                        <div className="loading"></div>
                        Adding...
                      </>
                    ) : (
                      'Add to Cart'
                    )}
                  </button>
                  
                  <Link to="/cart" className="btn btn-outline btn-large">
                    View Cart
                  </Link>
                </div>
              </div>
            )}

            <div className="product-features">
              <h3>Features & Benefits</h3>
              <ul>
                <li>Premium quality materials</li>
                <li>Custom design options available</li>
                <li>Bulk order discounts</li>
                <li>Fast delivery</li>
                <li>Quality guarantee</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Toast */}
      {showNotification && (
        <div className="notification-toast">
          <div className="notification-content">
            <span className="notification-icon">check</span>
            <span className="notification-message">
              {quantity} {quantity > 1 ? 'items' : 'item'} added to cart!
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
